"use client";

import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  TrendingUp,
  Zap,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clock,
  ShieldAlert,
  Target,
  ListChecks,
  BarChart3,
  Download,
  FileJson,
  FileText,
  Play,
  X,
  Loader2,
  CheckCheck,
  XCircle,
  Activity,
  Mail,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useAppToast } from '@/lib/use-app-toast';

interface ThreatScore {
  id: string;
  name: string;
  score: number;
  maxScore: number;
  category: string;
  trend: 'up' | 'down' | 'stable';
  delta: number;
}

interface PriorityRecommendation {
  rank: number;
  title: string;
  impact: 'critical' | 'high' | 'medium';
  effort: 'low' | 'medium' | 'high';
  deadline: string;
  description: string;
}

interface AutoResponse {
  id: string;
  trigger: string;
  action: string;
  status: 'ready' | 'active' | 'pending';
  type: 'block' | 'alert' | 'isolate' | 'rotate';
}

interface ThreatTimelineEntry {
  time: string;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

type ExecutionStatus = 'idle' | 'confirming' | 'executing' | 'success' | 'failed';

interface ExecutionState {
  status: ExecutionStatus;
  progress: number;
  log: string[];
  startedAt?: string;
  completedAt?: string;
}

const threatScores: ThreatScore[] = [
  { id: 'ts1', name: 'Overall Risk Score', score: 74, maxScore: 100, category: 'Composite', trend: 'up', delta: 6 },
  { id: 'ts2', name: 'Network Exposure', score: 88, maxScore: 100, category: 'Infrastructure', trend: 'up', delta: 12 },
  { id: 'ts3', name: 'Data Exfiltration Risk', score: 91, maxScore: 100, category: 'Data', trend: 'up', delta: 18 },
  { id: 'ts4', name: 'Credential Compromise', score: 63, maxScore: 100, category: 'Identity', trend: 'down', delta: 5 },
  { id: 'ts5', name: 'Endpoint Vulnerability', score: 57, maxScore: 100, category: 'Endpoint', trend: 'stable', delta: 0 },
];

const priorityRecommendations: PriorityRecommendation[] = [
  {
    rank: 1,
    title: 'Patch CVE-2026-0847 on DB-PROD-01',
    impact: 'critical',
    effort: 'low',
    deadline: 'Today',
    description: 'PostgreSQL 14.2 privilege escalation directly linked to active exfiltration THR-2845. Upgrade to 14.12 immediately.',
  },
  {
    rank: 2,
    title: 'Rotate all DB credentials',
    impact: 'critical',
    effort: 'low',
    deadline: 'Next 2 hrs',
    description: 'Attacker has active session via compromised svc_app credentials. Rotate svc_app, svc_backup, and admin accounts.',
  },
  {
    rank: 3,
    title: 'Enable WORM log storage (§18)',
    impact: 'high',
    effort: 'medium',
    deadline: '14 days',
    description: 'BD Cyber Security Act §18 requires tamper-proof 365-day log retention. Current retention is only 47 days.',
  },
  {
    rank: 4,
    title: 'Block Tor exit node range',
    impact: 'high',
    effort: 'low',
    deadline: 'Today',
    description: 'Firewall rule to block 185.220.101.0/24 (Tor exit nodes) at perimeter to cut C2 channel for THR-2845.',
  },
];

const autoResponses: AutoResponse[] = [
  { id: 'ar1', trigger: 'THR-2845 · Data Exfil Detected', action: 'Block outbound 185.220.101.0/24', status: 'active', type: 'block' },
  { id: 'ar2', trigger: 'Brute Force > 50 attempts/min', action: 'Geo-block source IP + alert SOC', status: 'ready', type: 'alert' },
  { id: 'ar3', trigger: 'Privilege Escalation on any host', action: 'Isolate host from network segment', status: 'ready', type: 'isolate' },
  { id: 'ar4', trigger: 'SQL Injection on PROD-API-01', action: 'Rotate DB credentials + WAF block', status: 'pending', type: 'rotate' },
];

const threatTimeline: ThreatTimelineEntry[] = [
  { time: '00:00', critical: 2, high: 5, medium: 8, low: 12 },
  { time: '02:00', critical: 1, high: 4, medium: 7, low: 10 },
  { time: '04:00', critical: 3, high: 6, medium: 9, low: 11 },
  { time: '06:00', critical: 2, high: 8, medium: 12, low: 15 },
  { time: '08:00', critical: 5, high: 11, medium: 14, low: 18 },
  { time: '10:00', critical: 4, high: 9, medium: 13, low: 16 },
  { time: '12:00', critical: 6, high: 13, medium: 17, low: 20 },
  { time: '14:00', critical: 3, high: 10, medium: 15, low: 19 },
  { time: '16:00', critical: 7, high: 14, medium: 18, low: 22 },
  { time: '18:00', critical: 5, high: 12, medium: 16, low: 21 },
  { time: '20:00', critical: 4, high: 11, medium: 14, low: 17 },
  { time: '22:00', critical: 3, high: 8, medium: 11, low: 14 },
];

// Simulated execution log steps per response type
const executionSteps: Record<string, string[]> = {
  block: [
    'Connecting to firewall API…',
    'Validating CIDR range 185.220.101.0/24…',
    'Pushing block rule to perimeter firewall…',
    'Verifying rule propagation across nodes…',
    'Rule active — outbound traffic blocked.',
  ],
  alert: [
    'Resolving source IP geolocation…',
    'Applying geo-block policy to edge router…',
    'Dispatching SOC alert via PagerDuty…',
    'Creating incident ticket #INC-7291…',
    'SOC notified — geo-block enforced.',
  ],
  isolate: [
    'Identifying affected host on network segment…',
    'Revoking host network access via NAC…',
    'Pushing isolation VLAN policy…',
    'Confirming host is unreachable from segment…',
    'Host isolated — containment complete.',
  ],
  rotate: [
    'Initiating credential rotation for svc_app…',
    'Generating new credentials via Vault…',
    'Updating WAF block rule for PROD-API-01…',
    'Propagating new credentials to services…',
    'Rotation complete — WAF rule active.',
  ],
};

function ScoreBar({ score, maxScore, color }: { score: number; maxScore: number; color: string }) {
  const pct = Math.round((score / maxScore) * 100);
  return (
    <div className="w-full bg-white/5 rounded-full h-1.5 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-700 ${color}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function scoreColor(score: number): string {
  if (score >= 80) return 'bg-cyber-red';
  if (score >= 60) return 'bg-orange-400';
  if (score >= 40) return 'bg-yellow-400';
  return 'bg-cyber-green';
}

function scoreTextColor(score: number): string {
  if (score >= 80) return 'text-cyber-red';
  if (score >= 60) return 'text-orange-400';
  if (score >= 40) return 'text-yellow-400';
  return 'text-cyber-green';
}

const impactColors: Record<string, string> = {
  critical: 'bg-cyber-red/15 text-cyber-red border-cyber-red/25',
  high: 'bg-orange-500/15 text-orange-400 border-orange-500/25',
  medium: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/25',
};

const effortColors: Record<string, string> = {
  low: 'text-cyber-green',
  medium: 'text-yellow-400',
  high: 'text-orange-400',
};

const responseTypeColors: Record<string, string> = {
  block: 'text-cyber-red bg-cyber-red/10 border-cyber-red/20',
  alert: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
  isolate: 'text-cyber-purple bg-cyber-purple/10 border-cyber-purple/20',
  rotate: 'text-cyber-blue bg-cyber-blue/10 border-cyber-blue/20',
};

const statusColors: Record<string, string> = {
  active: 'text-cyber-green bg-cyber-green/10 border-cyber-green/20',
  ready: 'text-cyber-blue bg-cyber-blue/10 border-cyber-blue/20',
  pending: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
};

function buildExportPayload() {
  return {
    exportedAt: new Date().toISOString(),
    severityScores: threatScores.map(ts => ({
      name: ts.name,
      category: ts.category,
      score: ts.score,
      maxScore: ts.maxScore,
      trend: ts.trend,
      delta: ts.delta,
    })),
    priorityRecommendations: priorityRecommendations.map(rec => ({
      rank: rec.rank,
      title: rec.title,
      impact: rec.impact,
      effort: rec.effort,
      deadline: rec.deadline,
      description: rec.description,
    })),
    automatedResponses: autoResponses.map(ar => ({
      trigger: ar.trigger,
      action: ar.action,
      status: ar.status,
      type: ar.type,
    })),
    threatTimeline: threatTimeline,
    riskSummary: {
      criticalThreats: 3,
      highThreats: 11,
      activeAutoResponses: autoResponses.filter(r => r.status === 'active').length,
      topPriority: priorityRecommendations[0]?.title ?? 'N/A',
    },
  };
}

function handleJsonExport() {
  const payload = buildExportPayload();
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `threat-analysis-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function handlePdfExport() {
  const payload = buildExportPayload();
  const ts = new Date().toLocaleString();

  const scoreRows = payload.severityScores
    .map(s => {
      const pct = Math.round((s.score / s.maxScore) * 100);
      const color = s.score >= 80 ? '#ef4444' : s.score >= 60 ? '#f97316' : s.score >= 40 ? '#eab308' : '#22c55e';
      return `
        <tr>
          <td>${s.name}</td>
          <td>${s.category}</td>
          <td style="color:${color};font-weight:700">${s.score}/${s.maxScore}</td>
          <td>
            <div style="background:#1e293b;border-radius:4px;height:8px;width:100%">
              <div style="background:${color};height:8px;border-radius:4px;width:${pct}%"></div>
            </div>
          </td>
          <td>${s.trend === 'stable' ? '—' : (s.trend === 'up' ? '▲' : '▼') + ' ' + s.delta}</td>
        </tr>`;
    })
    .join('');

  const recRows = payload.priorityRecommendations
    .map(r => {
      const ic = r.impact === 'critical' ? '#ef4444' : r.impact === 'high' ? '#f97316' : '#eab308';
      return `
        <tr>
          <td style="text-align:center;font-weight:700">${r.rank}</td>
          <td><strong>${r.title}</strong><br/><small style="color:#94a3b8">${r.description}</small></td>
          <td><span style="color:${ic};font-weight:600;text-transform:uppercase">${r.impact}</span></td>
          <td>${r.effort}</td>
          <td>${r.deadline}</td>
        </tr>`;
    })
    .join('');

  const arRows = payload.automatedResponses
    .map(ar => {
      const sc = ar.status === 'active' ? '#22c55e' : ar.status === 'ready' ? '#3b82f6' : '#eab308';
      return `
        <tr>
          <td>${ar.trigger}</td>
          <td>${ar.action}</td>
          <td style="text-transform:uppercase;font-weight:600">${ar.type}</td>
          <td><span style="color:${sc};font-weight:600;text-transform:uppercase">${ar.status}</span></td>
        </tr>`;
    })
    .join('');

  const tlRows = payload.threatTimeline
    .map(tl => `
      <tr>
        <td>${tl.time}</td>
        <td style="color:#ef4444;font-weight:600">${tl.critical}</td>
        <td style="color:#f97316;font-weight:600">${tl.high}</td>
        <td style="color:#eab308;font-weight:600">${tl.medium}</td>
        <td style="color:#22c55e;font-weight:600">${tl.low}</td>
      </tr>`)
    .join('');

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Threat Analysis Report</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; background: #0a0e1a; color: #e2e8f0; padding: 32px; font-size: 13px; }
    h1 { font-size: 22px; color: #fff; margin-bottom: 4px; }
    .meta { color: #64748b; font-size: 11px; margin-bottom: 28px; }
    h2 { font-size: 14px; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; margin: 24px 0 10px; border-bottom: 1px solid #1e293b; padding-bottom: 6px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
    th { background: #1e293b; color: #64748b; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; padding: 8px 10px; text-align: left; }
    td { padding: 8px 10px; border-bottom: 1px solid #1e293b; color: #cbd5e1; vertical-align: top; }
    tr:last-child td { border-bottom: none; }
    .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 8px; }
    .summary-card { background: #1e293b; border-radius: 8px; padding: 12px; }
    .summary-label { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
    .summary-value { font-size: 18px; font-weight: 700; }
    @media print { body { background: #0a0e1a !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <h1>🛡 Threat Analysis Report</h1>
  <p class="meta">Generated: ${ts} · Otek1 Syber Security Platform</p>

  <h2>Risk Summary</h2>
  <div class="summary-grid">
    <div class="summary-card"><div class="summary-label">Critical Threats</div><div class="summary-value" style="color:#ef4444">${payload.riskSummary.criticalThreats}</div></div>
    <div class="summary-card"><div class="summary-label">High Threats</div><div class="summary-value" style="color:#f97316">${payload.riskSummary.highThreats}</div></div>
    <div class="summary-card"><div class="summary-label">Active Auto-Responses</div><div class="summary-value" style="color:#22c55e">${payload.riskSummary.activeAutoResponses}</div></div>
    <div class="summary-card"><div class="summary-label">Top Priority</div><div class="summary-value" style="color:#3b82f6;font-size:12px">${payload.riskSummary.topPriority}</div></div>
  </div>

  <h2>Threat Severity Scores</h2>
  <table>
    <thead><tr><th>Metric</th><th>Category</th><th>Score</th><th>Progress</th><th>Trend</th></tr></thead>
    <tbody>${scoreRows}</tbody>
  </table>

  <h2>Priority Recommendations</h2>
  <table>
    <thead><tr><th>#</th><th>Action</th><th>Impact</th><th>Effort</th><th>Deadline</th></tr></thead>
    <tbody>${recRows}</tbody>
  </table>

  <h2>Automated Response Rules</h2>
  <table>
    <thead><tr><th>Trigger</th><th>Action</th><th>Type</th><th>Status</th></tr></thead>
    <tbody>${arRows}</tbody>
  </table>

  <h2>Threat Timeline (24h)</h2>
  <table>
    <thead><tr><th>Time</th><th style="color:#ef4444">Critical</th><th style="color:#f97316">High</th><th style="color:#eab308">Medium</th><th style="color:#22c55e">Low</th></tr></thead>
    <tbody>${tlRows}</tbody>
  </table>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => {
    win.print();
  }, 500);
}

// ── Confirmation Dialog ──────────────────────────────────────────────────────
interface ConfirmDialogProps {
  ar: AutoResponse;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDialog({ ar, onConfirm, onCancel }: ConfirmDialogProps) {
  const typeColor: Record<string, string> = {
    block: 'text-cyber-red border-cyber-red/30 bg-cyber-red/5',
    alert: 'text-orange-400 border-orange-500/30 bg-orange-500/5',
    isolate: 'text-cyber-purple border-cyber-purple/30 bg-cyber-purple/5',
    rotate: 'text-cyber-blue border-cyber-blue/30 bg-cyber-blue/5',
  };
  const btnColor: Record<string, string> = {
    block: 'bg-cyber-red/20 hover:bg-cyber-red/30 text-cyber-red border-cyber-red/30',
    alert: 'bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border-orange-500/30',
    isolate: 'bg-cyber-purple/20 hover:bg-cyber-purple/30 text-cyber-purple border-cyber-purple/30',
    rotate: 'bg-cyber-blue/20 hover:bg-cyber-blue/30 text-cyber-blue border-cyber-blue/30',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} />

      {/* Dialog */}
      <div className="relative w-full max-w-sm bg-cyber-dark border border-cyber-border rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-cyber-border">
          <div className="flex items-center gap-2">
            <Zap size={13} className="text-cyber-blue" />
            <span className="text-xs font-semibold text-white font-mono">Execute SOAR Response</span>
          </div>
          <button onClick={onCancel} className="text-cyber-muted hover:text-white transition-colors">
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4 space-y-3">
          {/* Warning banner */}
          <div className="flex items-start gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-3 py-2">
            <AlertTriangle size={11} className="text-yellow-400 shrink-0 mt-0.5" />
            <p className="text-[10px] font-mono text-yellow-400 leading-relaxed">
              This action will be executed immediately against live infrastructure. Confirm before proceeding.
            </p>
          </div>

          {/* Rule details */}
          <div className={`rounded-lg border px-3 py-2.5 space-y-2 ${typeColor[ar.type]}`}>
            <div className="flex items-center gap-1.5">
              <span className={`text-[9px] font-mono font-bold uppercase border rounded px-1.5 py-0.5 ${responseTypeColors[ar.type]}`}>
                {ar.type}
              </span>
              <span className="text-[10px] font-mono text-cyber-muted">Rule ID: {ar.id.toUpperCase()}</span>
            </div>
            <div>
              <p className="text-[9px] font-mono text-cyber-muted mb-0.5">TRIGGER</p>
              <p className="text-[11px] font-mono text-cyber-text">{ar.trigger}</p>
            </div>
            <div>
              <p className="text-[9px] font-mono text-cyber-muted mb-0.5">ACTION</p>
              <p className="text-[11px] font-semibold text-white">{ar.action}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-4 py-3 border-t border-cyber-border bg-white/2">
          <button
            onClick={onCancel}
            className="flex-1 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-cyber-border text-cyber-muted hover:text-white transition-colors text-[11px] font-mono font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors text-[11px] font-mono font-semibold ${btnColor[ar.type]}`}
          >
            <Play size={10} />
            Execute Now
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Execution Status Tracker ─────────────────────────────────────────────────
interface ExecutionTrackerProps {
  ar: AutoResponse;
  execState: ExecutionState;
  onDismiss: () => void;
}

function ExecutionTracker({ ar, execState, onDismiss }: ExecutionTrackerProps) {
  const isRunning = execState.status === 'executing';
  const isDone = execState.status === 'success' || execState.status === 'failed';

  return (
    <div className="mt-2 rounded-lg border border-cyber-border bg-black/30 overflow-hidden">
      {/* Status header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-cyber-border/50">
        <div className="flex items-center gap-1.5">
          {isRunning && <Loader2 size={10} className="text-cyber-blue animate-spin" />}
          {execState.status === 'success' && <CheckCheck size={10} className="text-cyber-green" />}
          {execState.status === 'failed' && <XCircle size={10} className="text-cyber-red" />}
          <span className={`text-[10px] font-mono font-semibold ${
            isRunning ? 'text-cyber-blue' :
            execState.status === 'success'? 'text-cyber-green' : 'text-cyber-red'
          }`}>
            {isRunning ? 'Executing…' : execState.status === 'success' ? 'Execution Complete' : 'Execution Failed'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {execState.startedAt && (
            <span className="text-[9px] font-mono text-cyber-muted">{execState.startedAt}</span>
          )}
          {isDone && (
            <button onClick={onDismiss} className="text-cyber-muted hover:text-white transition-colors">
              <X size={10} />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {isRunning && (
        <div className="h-0.5 bg-white/5">
          <div
            className="h-full bg-cyber-blue transition-all duration-300"
            style={{ width: `${execState.progress}%` }}
          />
        </div>
      )}

      {/* Log output */}
      <div className="px-3 py-2 space-y-1 max-h-28 overflow-y-auto scrollbar-cyber">
        {execState.log.map((line, i) => (
          <div key={i} className="flex items-start gap-1.5">
            <Activity size={8} className={`shrink-0 mt-0.5 ${
              i === execState.log.length - 1 && isRunning ? 'text-cyber-blue animate-pulse' : 'text-cyber-muted'
            }`} />
            <p className={`text-[10px] font-mono leading-tight ${
              i === execState.log.length - 1 && execState.status === 'success' ? 'text-cyber-green' :
              i === execState.log.length - 1 && execState.status === 'failed'? 'text-cyber-red' : 'text-cyber-muted'
            }`}>
              {line}
            </p>
          </div>
        ))}
      </div>

      {/* Completion timestamp */}
      {isDone && execState.completedAt && (
        <div className="px-3 py-1.5 border-t border-cyber-border/50 bg-white/2">
          <p className="text-[9px] font-mono text-cyber-muted">
            Completed at {execState.completedAt} · Rule {ar.id.toUpperCase()}
          </p>
        </div>
      )}
    </div>
  );
}

export default function ThreatAnalysisPanel() {
  const [openSection, setOpenSection] = useState<string>('scoring');
  const [exporting, setExporting] = useState<'pdf' | 'json' | null>(null);
  const [sendingEmail, setSendingEmail] = useState<Record<string, boolean>>({});

  // Execution state per response rule
  const [execStates, setExecStates] = useState<Record<string, ExecutionState>>({});
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const toast = useAppToast();
  const supabase = createClient();

  const toggle = (s: string) => setOpenSection(prev => (prev === s ? '' : s));

  const onJsonExport = () => {
    setExporting('json');
    try {
      handleJsonExport();
    } finally {
      setTimeout(() => setExporting(null), 800);
    }
  };

  const onPdfExport = () => {
    setExporting('pdf');
    try {
      handlePdfExport();
    } finally {
      setTimeout(() => setExporting(null), 800);
    }
  };

  // Open confirmation dialog
  const handleTriggerClick = (arId: string) => {
    setConfirmingId(arId);
  };

  // Cancel confirmation
  const handleCancelConfirm = () => {
    setConfirmingId(null);
  };

  // Confirm and start execution
  const handleConfirmExecution = (ar: AutoResponse) => {
    setConfirmingId(null);
    const steps = executionSteps[ar.type] ?? ['Executing action…', 'Action complete.'];
    const startedAt = new Date().toLocaleTimeString();

    setExecStates(prev => ({
      ...prev,
      [ar.id]: { status: 'executing', progress: 0, log: [], startedAt },
    }));

    // Simulate step-by-step execution
    steps.forEach((step, i) => {
      const delay = (i + 1) * 900;
      setTimeout(() => {
        setExecStates(prev => ({
          ...prev,
          [ar.id]: {
            ...prev[ar.id],
            progress: Math.round(((i + 1) / steps.length) * 100),
            log: steps.slice(0, i + 1),
          },
        }));

        // Final step
        if (i === steps.length - 1) {
          const completedAt = new Date().toLocaleTimeString();
          // 90% success rate simulation
          const success = Math.random() > 0.1;
          const finalLog = success
            ? steps
            : [...steps.slice(0, -1), '✗ Connection timeout — action may be incomplete.'];
          const finalStatus: ExecutionStatus = success ? 'success' : 'failed';

          setTimeout(() => {
            setExecStates(prev => ({
              ...prev,
              [ar.id]: {
                ...prev[ar.id],
                status: finalStatus,
                progress: 100,
                log: finalLog,
                completedAt,
              },
            }));

            // ── In-app toast notification ──
            if (success) {
              toast.success(
                `SOAR Response Complete — ${ar.id.toUpperCase()}`,
                `${ar.action} executed successfully at ${completedAt}`
              );
            } else {
              toast.error(
                `SOAR Response Failed — ${ar.id.toUpperCase()}`,
                `${ar.action} encountered an error. Retry recommended.`
              );
            }

            // ── Send email alert via Edge Function ──
            sendSoarAlertEmail({
              ar,
              status: finalStatus,
              log: finalLog,
              startedAt,
              completedAt,
            });
          }, 300);
        }
      }, delay);
    });
  };

  // Send SOAR alert email via Supabase Edge Function
  const sendSoarAlertEmail = async ({
    ar,
    status,
    log,
    startedAt,
    completedAt,
  }: {
    ar: AutoResponse;
    status: ExecutionStatus;
    log: string[];
    startedAt: string;
    completedAt: string;
  }) => {
    try {
      setSendingEmail(prev => ({ ...prev, [ar.id]: true }));

      // Get current user email
      const { data: { user } } = await supabase.auth.getUser();
      const to = user?.email ?? 'admin@otek1syber.com';

      const { data, error } = await supabase.functions.invoke('soar-alert-email', {
        body: {
          to,
          ruleId: ar.id,
          ruleType: ar.type,
          trigger: ar.trigger,
          action: ar.action,
          status,
          startedAt,
          completedAt,
          log,
          siteUrl: process.env.NEXT_PUBLIC_SITE_URL ?? 'https://otek1sybe7575.builtwithrocket.new',
        },
      });

      if (error) throw error;

      if (data?.success) {
        toast.info(
          'Alert Email Sent',
          `SOAR completion report delivered to ${to}`
        );
      }
    } catch (err) {
      console.error('SOAR email alert failed:', err);
      toast.alert(
        'Email Alert Failed',
        'Could not send SOAR completion email. Check your Resend configuration.'
      );
    } finally {
      setSendingEmail(prev => ({ ...prev, [ar.id]: false }));
    }
  };

  // Dismiss completed tracker
  const handleDismissTracker = (arId: string) => {
    setExecStates(prev => {
      const next = { ...prev };
      delete next[arId];
      return next;
    });
  };

  const confirmingAr = confirmingId ? autoResponses.find(r => r.id === confirmingId) : null;

  return (
    <div className="flex flex-col gap-3 h-full overflow-y-auto scrollbar-cyber">

      {/* ── Confirmation Dialog (portal-style overlay) ── */}
      {confirmingAr && (
        <ConfirmDialog
          ar={confirmingAr}
          onConfirm={() => handleConfirmExecution(confirmingAr)}
          onCancel={handleCancelConfirm}
        />
      )}

      {/* ── Export Buttons ── */}
      <div className="cyber-card px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Download size={11} className="text-cyber-muted" />
            <span className="text-[10px] text-cyber-muted uppercase tracking-widest font-mono">Export Report</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onJsonExport}
            disabled={exporting !== null}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyber-blue/10 border border-cyber-blue/25 text-cyber-blue hover:bg-cyber-blue/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-[11px] font-mono font-medium"
          >
            <FileJson size={12} />
            {exporting === 'json' ? 'Exporting…' : 'Export JSON'}
          </button>
          <button
            onClick={onPdfExport}
            disabled={exporting !== null}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyber-purple/10 border border-cyber-purple/25 text-cyber-purple hover:bg-cyber-purple/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-[11px] font-mono font-medium"
          >
            <FileText size={12} />
            {exporting === 'pdf' ? 'Opening…' : 'Export PDF'}
          </button>
        </div>
        <p className="text-[9px] font-mono text-cyber-muted mt-2">
          Captures severity scores · priority recommendations · automated responses · threat timeline
        </p>
      </div>

      {/* ── Threat Severity Scoring ── */}
      <div className="cyber-card overflow-hidden">
        <button
          onClick={() => toggle('scoring')}
          aria-expanded={openSection === 'scoring'}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/3 transition-colors"
        >
          <div className="flex items-center gap-2">
            <BarChart3 size={13} className="text-cyber-red" />
            <span className="text-xs font-semibold text-white">Threat Severity Scoring</span>
            <span className="text-[10px] font-mono bg-cyber-red/15 text-cyber-red border border-cyber-red/25 rounded-full px-1.5 py-0.5">
              {threatScores.length}
            </span>
          </div>
          {openSection === 'scoring' ? <ChevronUp size={13} className="text-cyber-muted" /> : <ChevronDown size={13} className="text-cyber-muted" />}
        </button>

        {openSection === 'scoring' && (
          <div className="border-t border-cyber-border px-4 py-3 space-y-3">
            {threatScores.map((ts) => (
              <div key={ts.id}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-cyber-text">{ts.name}</span>
                    <span className="text-[9px] font-mono text-cyber-muted bg-white/5 border border-cyber-border rounded px-1 py-0.5">
                      {ts.category}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {ts.trend !== 'stable' && (
                      <div className={`flex items-center gap-0.5 text-[10px] font-mono ${ts.trend === 'up' ? 'text-cyber-red' : 'text-cyber-green'}`}>
                        <TrendingUp size={9} className={ts.trend === 'down' ? 'rotate-180' : ''} />
                        <span>{ts.delta > 0 ? '+' : ''}{ts.trend === 'down' ? '-' : ''}{ts.delta}</span>
                      </div>
                    )}
                    <span className={`text-[12px] font-bold font-mono ${scoreTextColor(ts.score)}`}>
                      {ts.score}
                    </span>
                    <span className="text-[10px] text-cyber-muted font-mono">/{ts.maxScore}</span>
                  </div>
                </div>
                <ScoreBar score={ts.score} maxScore={ts.maxScore} color={scoreColor(ts.score)} />
              </div>
            ))}

            {/* Legend */}
            <div className="flex items-center gap-3 pt-1 flex-wrap">
              {[
                { color: 'bg-cyber-red', label: 'Critical (80+)' },
                { color: 'bg-orange-400', label: 'High (60–79)' },
                { color: 'bg-yellow-400', label: 'Medium (40–59)' },
                { color: 'bg-cyber-green', label: 'Low (<40)' },
              ].map(({ color, label }) => (
                <div key={label} className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${color}`} />
                  <span className="text-[9px] font-mono text-cyber-muted">{label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Priority Recommendations ── */}
      <div className="cyber-card overflow-hidden">
        <button
          onClick={() => toggle('priority')}
          aria-expanded={openSection === 'priority'}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/3 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Target size={13} className="text-orange-400" />
            <span className="text-xs font-semibold text-white">Priority Recommendations</span>
            <span className="text-[10px] font-mono bg-orange-500/15 text-orange-400 border border-orange-500/25 rounded-full px-1.5 py-0.5">
              {priorityRecommendations.length}
            </span>
          </div>
          {openSection === 'priority' ? <ChevronUp size={13} className="text-cyber-muted" /> : <ChevronDown size={13} className="text-cyber-muted" />}
        </button>

        {openSection === 'priority' && (
          <div className="border-t border-cyber-border divide-y divide-cyber-border/50">
            {priorityRecommendations.map((rec) => (
              <div key={rec.rank} className="px-4 py-3 hover:bg-white/2 transition-colors">
                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-white/5 border border-cyber-border flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-[9px] font-bold font-mono text-cyber-muted">{rec.rank}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      <span className="text-[12px] font-medium text-white leading-tight">{rec.title}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                      <span className={`text-[9px] font-mono font-semibold border rounded px-1.5 py-0.5 uppercase ${impactColors[rec.impact]}`}>
                        {rec.impact}
                      </span>
                      <span className="text-[9px] font-mono text-cyber-muted">
                        Effort: <span className={effortColors[rec.effort]}>{rec.effort}</span>
                      </span>
                      <div className="flex items-center gap-0.5 text-[9px] font-mono text-cyber-muted">
                        <Clock size={8} />
                        <span>{rec.deadline}</span>
                      </div>
                    </div>
                    <p className="text-[11px] text-cyber-muted leading-relaxed">{rec.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Automated Response Suggestions ── */}
      <div className="cyber-card overflow-hidden">
        <button
          onClick={() => toggle('autoresponse')}
          aria-expanded={openSection === 'autoresponse'}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/3 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Zap size={13} className="text-cyber-blue" />
            <span className="text-xs font-semibold text-white">Automated Responses</span>
            <span className="text-[10px] font-mono bg-cyber-blue/15 text-cyber-blue border border-cyber-blue/25 rounded-full px-1.5 py-0.5">
              {autoResponses.filter(r => r.status === 'active').length} active
            </span>
          </div>
          {openSection === 'autoresponse' ? <ChevronUp size={13} className="text-cyber-muted" /> : <ChevronDown size={13} className="text-cyber-muted" />}
        </button>

        {openSection === 'autoresponse' && (
          <div className="border-t border-cyber-border divide-y divide-cyber-border/50">
            {autoResponses.map((ar) => {
              const exec = execStates[ar.id];
              const isExecuting = exec?.status === 'executing';
              const isDone = exec?.status === 'success' || exec?.status === 'failed';

              return (
                <div key={ar.id} className="px-4 py-3 hover:bg-white/2 transition-colors">
                  {/* Top row: badges + live dot + execute button */}
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`text-[9px] font-mono font-semibold border rounded px-1.5 py-0.5 uppercase ${responseTypeColors[ar.type]}`}>
                        {ar.type}
                      </span>
                      <span className={`text-[9px] font-mono border rounded px-1.5 py-0.5 uppercase ${statusColors[ar.status]}`}>
                        {ar.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {ar.status === 'active' && !exec && (
                        <div className="w-1.5 h-1.5 bg-cyber-green rounded-full live-dot" />
                      )}
                      {/* Execute trigger button */}
                      {!isExecuting && !isDone && (
                        <button
                          onClick={() => handleTriggerClick(ar.id)}
                          className="flex items-center gap-1 px-2 py-1 rounded-md bg-cyber-blue/10 hover:bg-cyber-blue/20 border border-cyber-blue/25 text-cyber-blue transition-colors text-[9px] font-mono font-semibold"
                          title="Execute this SOAR response"
                        >
                          <Play size={8} />
                          Execute
                        </button>
                      )}
                      {isExecuting && (
                        <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-cyber-blue/5 border border-cyber-blue/15 text-cyber-blue/60 text-[9px] font-mono">
                          <Loader2 size={8} className="animate-spin" />
                          Running
                        </div>
                      )}
                      {exec?.status === 'success' && (
                        <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-cyber-green/10 border border-cyber-green/20 text-cyber-green text-[9px] font-mono">
                          <CheckCheck size={8} />
                          Done
                          {sendingEmail[ar.id] && <Mail size={7} className="animate-pulse ml-0.5" />}
                        </div>
                      )}
                      {exec?.status === 'failed' && (
                        <button
                          onClick={() => handleTriggerClick(ar.id)}
                          className="flex items-center gap-1 px-2 py-1 rounded-md bg-cyber-red/10 hover:bg-cyber-red/20 border border-cyber-red/20 text-cyber-red transition-colors text-[9px] font-mono"
                          title="Retry execution"
                        >
                          <Play size={8} />
                          Retry
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Trigger & action */}
                  <div className="flex items-start gap-1.5 mb-1">
                    <AlertTriangle size={9} className="text-cyber-muted shrink-0 mt-0.5" />
                    <p className="text-[11px] text-cyber-muted font-mono leading-tight">{ar.trigger}</p>
                  </div>
                  <div className="flex items-start gap-1.5">
                    <CheckCircle2 size={9} className="text-cyber-blue shrink-0 mt-0.5" />
                    <p className="text-[11px] text-cyber-text leading-tight">{ar.action}</p>
                  </div>

                  {/* Real-time execution tracker */}
                  {exec && (
                    <ExecutionTracker
                      ar={ar}
                      execState={exec}
                      onDismiss={() => handleDismissTracker(ar.id)}
                    />
                  )}
                </div>
              );
            })}

            {/* Footer note */}
            <div className="px-4 py-2.5 bg-cyber-blue/5">
              <div className="flex items-center gap-1.5">
                <ShieldAlert size={10} className="text-cyber-blue" />
                <p className="text-[10px] font-mono text-cyber-muted">
                  Automated rules run via SOAR integration · <span className="text-cyber-blue">Configure in Settings</span>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Risk Summary Card */}
      <div className="cyber-card px-4 py-3">
        <div className="flex items-center gap-1.5 mb-2">
          <ListChecks size={11} className="text-cyber-purple" />
          <p className="text-[10px] text-cyber-muted uppercase tracking-widest font-mono">Risk Summary</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: 'Critical Threats', value: '3', color: 'text-cyber-red' },
            { label: 'High Threats', value: '11', color: 'text-orange-400' },
            { label: 'Auto-Responses', value: `${autoResponses.filter(r => r.status === 'active').length} active`, color: 'text-cyber-green' },
            { label: 'Top Priority', value: 'Patch DB', color: 'text-cyber-blue' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white/3 border border-cyber-border rounded-lg px-2.5 py-2">
              <p className="text-[9px] font-mono text-cyber-muted mb-0.5">{label}</p>
              <p className={`text-[12px] font-bold font-mono ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
